/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { IoMdClose } from "react-icons/io";
import { useRecoilState } from "recoil";
import { UserAtom } from "../../atoms/UserAtom";
import useClickOutside from "../../hook/useClickOutside";
import { ILink } from "../../types/LinkTypes";
import { ITag } from "../../types/TagTypes";
import { getScrollParent } from "../../utils/DOMHelper";

const Form = styled.div`
  width: 100%;
  display: flex;
  position: relative;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

const StyledClearIcon = styled.div<{ isCreatingTag: boolean }>`
  position: absolute;
  cursor: pointer;
  right: ${(props) => (props.isCreatingTag ? "80px" : "14px")};
  top: 10px;
  height: 22px;
  width: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.3s;

  :hover {
    background-color: #22222217;
  }
`;

interface InputProps {
  active: boolean;
}

const Input = styled.input<InputProps>`
  width: 100%;
  height: 42px;
  border: 1px solid #dddddd;
  border-radius: 5px;
  outline: none;
  font-size: 16px;
  padding: 5px;
  color: #383838;
  text-indent: 75px;
  font-family: "Source Sans Pro" !important;

  :focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border: 1px solid #fb972e;
  }

  ${(props) =>
    props.active &&
    css`
      box-shadow: 0px 0px 6px #fb972e4d;
      border: 1px solid #fb972e;
    `}

  &::placeholder {
    color: #979797;
  }
  background-color: #ffffff;
`;

const AutoCompleteDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  border-radius: 5px;
  background-color: #ffffff;
`;

export const SystemTag = styled.div`
  font-size: 11px;
  background-color: #eceeef;
  color: #9d9e9e;
  margin-left: 10px;
  width: 50px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Cancel = styled.span`
  font: normal normal 600 15px;
  position: absolute;
  cursor: pointer;
  left: 6px;
  top: 6px;
  align-items: center;
  justify-content: center;
  width: 70px;
  font-weight: 600;
  height: 30px;
  display: flex;
  background-color: #fff2e5;
  border-radius: 4px;
  color: #fb972e;
  border: 1px solid #fb972e;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently
                                  supported by Chrome, Edge, Opera and Firefox */
`;

export interface ILinkOption extends ILink {}

type IStyledAutocompleteSelectProps = {
  placeholder?: string;
  absolutePosition?: boolean;
  createOnly?: boolean;
  linkInputRef?: React.MutableRefObject<HTMLInputElement>;
  // suggestions: ILinkOption[] | undefined;
  selectedValue: string;
  onEnter: (tag: ITag) => void;
  onValueChange?: (value: string) => void;
  onValueSelect: (tag: ITag) => void;
  viewCategories?: () => void;
  links: ILink[];
  deleteTag: (tag: ILink) => any;
  disabled?: boolean;
};

export const FindLinkRegex =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)/;

export default function AutoCompleteInterestTag(
  props: IStyledAutocompleteSelectProps
): JSX.Element {
  const {
    linkInputRef,
    selectedValue,
    onValueSelect,
    onValueChange,
    placeholder,
    links,
    deleteTag,
    createOnly,
    disabled,
  } = props;
  /**
   * Select functionality
   */
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  /**
   * Refs to keep track
   */
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const [offsetPopover, setOffsetPopover] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const dropViewRef = useRef<HTMLDivElement | null>(null);

  const [dropViewPosition, setDropViewPosition] = useState<{
    top: number;
    left: number;
  }>({
    top: 0,
    left: 0,
  });
  const [dropViewWidth, setDropViewWidth] = useState<number>(0);
  const [tagToCreate, setTagToCreate] = useState<string>("");
  const [isHttps, setIsHttps] = useState(true);

  const [user] = useRecoilState(UserAtom);
  /**
   * Update DropView position to match current Input location on screen.
   *
   * This function moves the DropView below the Input position.
   */
  const updateDropViewPosition = useCallback(() => {
    const elementBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    setDropViewPosition({
      top: elementBoundingRect.top + elementBoundingRect.height,
      left: elementBoundingRect.left,
    });

    setDropViewWidth(elementBoundingRect.width);
  }, []);

  useEffect(() => {
    if (!dropViewRef.current || !wrapperRef.current) return;

    const elementBoundingRect = (
      dropViewRef.current as HTMLDivElement
    ).getBoundingClientRect();

    const containerBoundingRect = (
      wrapperRef.current as HTMLDivElement
    ).getBoundingClientRect();

    let bottom = elementBoundingRect.bottom;

    if (offsetPopover !== 0)
      bottom =
        bottom + elementBoundingRect.height + containerBoundingRect.height;

    if (bottom >= 0 && bottom >= window.innerHeight) {
      setOffsetPopover(window.innerHeight - containerBoundingRect.top);
    } else {
      setOffsetPopover(0);
    }
  }, [dropViewRef, openDropView, selectedValue]);

  /**
   * useEffect that closes the DropView when a scroll is detected, much like
   * the native <select />
   */
  useEffect(() => {
    function closeDropView() {
      setOpenDropView(false);
    }

    const scrollParent = getScrollParent(
      containerRef.current,
      containerRef.current
    );

    if (!scrollParent) return () => {};

    scrollParent.addEventListener("scroll", closeDropView);

    return () => {
      scrollParent.removeEventListener("scroll", closeDropView);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRef, openDropView]);

  /**
   * On window resize, calculate position
   */
  useEffect(() => {
    window.addEventListener("resize", updateDropViewPosition);
    return () => window.removeEventListener("resize", updateDropViewPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formRef = useClickOutside(() => {
    const [tag] = links;
    // The value he typed is already a value in the suggestion list
    if (tag) {
      setOpenDropView(false);
      onValueChange(tag.url);
      onValueSelect(tag);
    }
  });

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let { value } = event.target;
      let https = isHttps;

      if (value.toLowerCase().indexOf("https://") === 0) {
        https = true;
      } else if (value.toLowerCase().indexOf("http://") === 0) {
        https = false;
        // Edge case - Copied from unlinked links
      } else if (value.toLowerCase().indexOf("h‌ttps://") === 0) {
        https = true;
      } else if (value.toLowerCase().indexOf("h‌ttp://") === 0) {
        https = false;
      }

      value = value
        .replace(/^https?:\/\//gi, "")
        .replace(/^h‌ttps?:\/\//gi, "");
      value = (https ? "https://" : "http://") + value;

      // setCurrentFocus(-1);
      setOpenDropView(true);

      if (onValueChange) onValueChange(value);
    },
    [isHttps]
  );

  function clearInput() {
    if (!onValueChange) return;
    onValueChange("");
    const [tag] = links;
    if (tag) deleteTag(tag);
  }

  useEffect(() => {
    // @ts-ignore
    onValueChange((selectedValue) => {
      return selectedValue.replace(
        !isHttps ? "https://" : "http://",
        isHttps ? "https://" : "http://"
      );
    });
  }, [isHttps]);

  useEffect(() => {
    let isHttpsTempValue = null;
    if (
      selectedValue.toLowerCase().indexOf("http://") === 0 ||
      // Edge case - Copied from unlinked links
      selectedValue.toLowerCase().indexOf("h‌ttp://") === 0
    ) {
      isHttpsTempValue = false;
    } else if (
      selectedValue.toLowerCase().indexOf("https://") === 0 ||
      // Edge case - Copied from unlinked links
      selectedValue.toLowerCase().indexOf("h‌ttps://") === 0
    ) {
      isHttpsTempValue = true;
    }
    if (isHttpsTempValue !== null) setIsHttps(isHttpsTempValue);
  }, [selectedValue]);

  function printUnprefixedURL() {
    if (selectedValue.toLowerCase().indexOf("h‌ttps://") === 0) {
      return selectedValue.replace("h‌ttps://", "").replace("H‌ttps://", "");
    } else if (selectedValue.toLowerCase().indexOf("h‌ttp://") === 0) {
      return selectedValue.replace("h‌ttp://", "").replace("H‌ttp://", "");
    }

    return selectedValue.replace(/^https?:\/\//gi, "");
  }

  return (
    <Form ref={formRef}>
      <AutoCompleteDiv ref={containerRef}>
        <InputContainer ref={wrapperRef}>
          <Input
            ref={linkInputRef}
            active={openDropView}
            value={printUnprefixedURL()}
            onChange={handleInputChange}
            type="text"
            placeholder={placeholder}
            autoComplete="off"
            css={css`
              /* text-indent: ${!!user?.options?.interestTagging &&
              !disabled &&
              !createOnly
                ? "25px"
                : "0"}; */
              padding-left: ${!!user?.options?.interestTagging &&
              !disabled &&
              !createOnly
                ? "5px"
                : "10px"};
            `}
          />
          <Cancel
            onClick={() => {
              setIsHttps(!isHttps);
            }}
          >
            {isHttps ? "https://" : "http://"}
          </Cancel>
        </InputContainer>
      </AutoCompleteDiv>
      {selectedValue.length > 0 && !disabled && (
        <StyledClearIcon
          isCreatingTag={!!tagToCreate.length}
          onClick={(e) => {
            e.stopPropagation();
            clearInput();
          }}
        >
          <IoMdClose color="#777777" size={16} />
        </StyledClearIcon>
      )}
    </Form>
  );
}
